import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Play, Pause, RotateCcw, CheckCircle2 } from "lucide-react";

const PHASES = ["Inhale", "Hold", "Exhale", "HoldEmpty"];

export default function PracticePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [practice, setPractice] = useState(null);
  const [running, setRunning] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [cycle, setCycle] = useState(1);
  const [showLog, setShowLog] = useState(false);
  const [pre, setPre] = useState({ stress: 5, energy: 5, focus: 5 });
  const [post, setPost] = useState({ stress: 5, energy: 5, focus: 5, sleep: null });
  const [stage, setStage] = useState("pre"); // pre | session | post
  const tickRef = useRef(null);

  useEffect(() => {
    api.get(`/practices/${id}`).then((r) => setPractice(r.data));
  }, [id]);

  const phaseDuration = (p) => {
    if (!practice) return 0;
    if (p === "Inhale") return practice.inhale_seconds;
    if (p === "Hold") return practice.hold_seconds;
    if (p === "Exhale") return practice.exhale_seconds;
    if (p === "HoldEmpty") return practice.hold_seconds > 0 ? Math.max(2, Math.floor(practice.hold_seconds / 2)) : 0;
    return 0;
  };

  // start session timer
  useEffect(() => {
    if (!running || !practice) return;
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s > 1) return s - 1;
        // advance phase
        advancePhase();
        return 0;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, practice, phaseIdx, cycle]);

  const advancePhase = () => {
    let next = phaseIdx + 1;
    if (next >= PHASES.length) {
      next = 0;
      if (cycle >= practice.cycles) {
        finishSession();
        return;
      }
      setCycle((c) => c + 1);
    }
    // skip zero-duration phases
    let nextPhase = PHASES[next];
    let dur = phaseDuration(nextPhase);
    while (dur === 0) {
      next = (next + 1) % PHASES.length;
      if (next === 0) setCycle((c) => c + 1);
      nextPhase = PHASES[next];
      dur = phaseDuration(nextPhase);
      if (next === phaseIdx) break;
    }
    setPhaseIdx(next);
    setSecondsLeft(dur);
  };

  const startSession = () => {
    setStage("session");
    setPhaseIdx(0);
    setCycle(1);
    setSecondsLeft(phaseDuration("Inhale"));
    setRunning(true);
  };

  const togglePause = () => setRunning((r) => !r);

  const reset = () => {
    setRunning(false);
    setPhaseIdx(0);
    setCycle(1);
    setSecondsLeft(0);
    setStage("pre");
  };

  const finishSession = () => {
    setRunning(false);
    if (tickRef.current) clearInterval(tickRef.current);
    setStage("post");
    setShowLog(true);
  };

  const saveLog = async () => {
    try {
      await api.post("/sessions", {
        practice_id: practice.id,
        practice_title: practice.title,
        stress_before: pre.stress, stress_after: post.stress,
        energy_before: pre.energy, energy_after: post.energy,
        focus_before: pre.focus, focus_after: post.focus,
        sleep_quality: post.sleep,
      });
      toast.success("Session saved.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!practice) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading practice...</div>;
  }

  const currentPhase = PHASES[phaseIdx];
  const isInhale = currentPhase === "Inhale";
  const isExhale = currentPhase === "Exhale";
  const scale = isInhale ? 1.5 : isExhale ? 0.85 : 1.15;
  const phaseLabel = currentPhase === "HoldEmpty" ? "Hold" : currentPhase;
  const phaseDur = phaseDuration(currentPhase) || 1;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-10">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-primary" data-testid="player-back-btn">← Back</button>

      <div className="mt-6 flex flex-col items-center text-center">
        <p className="overline text-primary mb-2">{practice.category}</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{practice.title}</h1>
        <p className="mt-3 max-w-xl text-sm sm:text-base text-muted-foreground">{practice.purpose}</p>
        <p className="mt-3 text-xs text-muted-foreground">{practice.duration_minutes} min · {practice.difficulty} · {practice.cycles} cycles</p>
      </div>

      {/* Pre-state */}
      <AnimatePresence mode="wait">
        {stage === "pre" && (
          <motion.div key="pre" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-8 sm:mt-12">
            <Card className="p-5 sm:p-8 max-w-2xl mx-auto">
              <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight">How do you feel right now?</h2>
              <p className="text-sm text-muted-foreground mt-1">Rate each from 1 (low) to 10 (high). We'll compare with post-session.</p>
              <div className="mt-6 sm:mt-8 space-y-6 sm:space-y-7">
                <SliderRow label="Stress level" value={pre.stress} onChange={(v) => setPre({ ...pre, stress: v })} testid="pre-stress" />
                <SliderRow label="Energy level" value={pre.energy} onChange={(v) => setPre({ ...pre, energy: v })} testid="pre-energy" />
                <SliderRow label="Focus level" value={pre.focus} onChange={(v) => setPre({ ...pre, focus: v })} testid="pre-focus" />
              </div>
              <div className="mt-8 sm:mt-10 flex justify-end">
                <Button size="lg" className="w-full sm:w-auto rounded-full" onClick={startSession} data-testid="start-session-btn">Begin session</Button>
              </div>
              <div className="mt-8 sm:mt-10 pt-6 border-t border-border">
                <h3 className="text-sm font-semibold mb-2">Instructions</h3>
                <ul className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                  {practice.instructions.map((step, i) => <li key={i}>{step}</li>)}
                </ul>
              </div>
            </Card>
          </motion.div>
        )}

        {stage === "session" && (
          <motion.div key="session" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-8 sm:mt-12 flex flex-col items-center">
            <div className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-secondary/40 blur-2xl" />
              <motion.div
                animate={{ scale }}
                transition={{ duration: phaseDur, ease: "easeInOut" }}
                className="absolute inset-6 rounded-full bg-secondary/70 border border-secondary"
                style={{ boxShadow: "0 0 80px 10px hsl(var(--secondary) / 0.6)" }}
              />
              <div className="relative z-10 text-center">
                <p className="overline text-primary">{phaseLabel}</p>
                <p className="font-display text-5xl sm:text-6xl font-semibold tracking-tight text-primary mt-2" data-testid="phase-counter">{secondsLeft}</p>
                <p className="text-xs text-muted-foreground mt-3">Cycle {cycle} of {practice.cycles}</p>
              </div>
            </div>

            <div className="mt-10 sm:mt-12 flex items-center gap-2 sm:gap-3 flex-wrap justify-center px-4">
              <Button variant="outline" size="lg" className="rounded-full" onClick={reset} data-testid="player-reset-btn">
                <RotateCcw className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Reset</span>
              </Button>
              <Button size="lg" className="rounded-full px-6 sm:px-8" onClick={togglePause} data-testid="player-toggle-btn">
                {running ? <><Pause className="h-4 w-4 mr-2" /> Pause</> : <><Play className="h-4 w-4 mr-2" /> Resume</>}
              </Button>
              <Button variant="outline" size="lg" className="rounded-full" onClick={finishSession} data-testid="player-finish-btn">
                <CheckCircle2 className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Finish</span>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Post log dialog */}
      <Dialog open={showLog} onOpenChange={setShowLog}>
        <DialogContent className="sm:max-w-lg" data-testid="post-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Log how you feel now</DialogTitle>
            <DialogDescription>This is what makes progress visible. Takes 10 seconds.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-2">
            <SliderRow label="Stress level" value={post.stress} onChange={(v) => setPost({ ...post, stress: v })} testid="post-stress" />
            <SliderRow label="Energy level" value={post.energy} onChange={(v) => setPost({ ...post, energy: v })} testid="post-energy" />
            <SliderRow label="Focus level" value={post.focus} onChange={(v) => setPost({ ...post, focus: v })} testid="post-focus" />
            <SliderRow label="Sleep quality (last night)" value={post.sleep ?? 5} onChange={(v) => setPost({ ...post, sleep: v })} testid="post-sleep" />
          </div>
          <DialogFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={() => { setShowLog(false); navigate("/dashboard"); }} data-testid="post-skip-btn">Skip</Button>
            <Button onClick={saveLog} data-testid="post-save-btn">Save session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SliderRow({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="flex justify-between mb-2">
        <Label className="text-sm">{label}</Label>
        <span className="text-sm font-medium text-primary" data-testid={`${testid}-value`}>{value}/10</span>
      </div>
      <Slider value={[value]} min={1} max={10} step={1} onValueChange={(v) => onChange(v[0])} data-testid={`${testid}-slider`} />
    </div>
  );
}
